import { styled, TextField, Box } from '@mui/material';
import type { SxProps, InputProps, TextFieldProps } from '@mui/material';
import { AccessControl } from 'features/accessControl';
import { CommonLabelSeparateProps } from './types';
import { forwardRef } from 'react';
import { getInputStyles } from './utils/getInputStyles';

export type TextInputProps = CommonLabelSeparateProps & {
  autoFocus?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  defaultValue?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  id?: string;
  label?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
  required?: boolean;
  sx?: SxProps;
  type?: 'email' | 'hidden' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url';
  value?: string;
  variant?: 'filled' | 'outlined' | 'standard' | 'unstyled';
  InputProps?: InputProps;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  helperText?: string;
  autoComplete?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  size?: TextFieldProps['size'];
  onClick?: TextFieldProps['onClick'];
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      autoFocus,
      color,
      defaultValue,
      disabled,
      error,
      fullWidth,
      id,
      label,
      name,
      onChange,
      placeholder,
      required,
      sx,
      type,
      value,
      variant = 'standard',
      helperText,
      onBlur,
      onFocus,
      onPaste,
      ...props
    },
    ref
  ) => {
    return (
      <StyledTextField
        InputLabelProps={{
          shrink: true,
          required,
        }}
        {...props}
        InputProps={{ ...props.InputProps }}
        autoFocus={autoFocus}
        color={color}
        defaultValue={defaultValue}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        id={id}
        label={label}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        inputRef={ref} // Forward the ref here
        required={false}
        sx={sx}
        type={type}
        value={value}
        variant={variant === 'unstyled' ? 'standard' : variant}
        onKeyDown={props.onKeyDown}
        onPaste={onPaste}
        helperText={helperText}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    );
  }
);

TextInput.displayName = 'TextInput';

export const StyledTextField = styled(
  ({
    labelSeparate,
    errorIconProps,
    errorIcon,
    helperText: hint,
    labelVariant,
    labelWeight,
    isUnderline,
    ...styledProps
  }: TextFieldProps & CommonLabelSeparateProps) => {
    const helperText =
      hint && styledProps?.error && errorIcon ? (
        <Box p={0} m={0} display="flex" alignItems="center" gap="4px">
          {errorIcon} {hint}
        </Box>
      ) : (
        hint
      );
    return <TextField {...styledProps} helperText={helperText} />;
  }
)(({ theme, labelSeparate, labelVariant, isUnderline, labelWeight, labelColor, ...rest }) => {
  return {
    ...getInputStyles({
      theme,
      labelSeparate,
      variant: rest?.variant,
      isUnderline,
      labelColor,
      labelVariant,
      labelWeight,
    }),
  };
});
